import { Lexer } from 'marked'

// Markdown as it streams in: where a reveal can safely stop, how to close what the tail has opened,
// and how to cut the text into blocks that can each be parsed and memoized on their own.

const FOOTNOTE_DEFINITION = /^ {0,3}\[\^[^\]]+\]:/m

/**
 * Splits markdown into its top-level blocks (a whole list, fenced code block, table or quote is one
 * block), with blank-line runs kept on the block before them, so the pieces always concatenate back
 * to `source`. Each can then be parsed and memoized on its own.
 *
 * Reference-style link definitions, footnotes and CRLF input resolve across blocks or are
 * normalized by the lexer, so those return the whole source as a single block.
 */
export function splitMarkdownBlocks(source: string): Array<string> {
  if (!source) {
    return []
  }
  if (source.includes('\r') || FOOTNOTE_DEFINITION.test(source)) {
    return [source]
  }

  // Blocks are cut from `source` by each token's length rather than taken from `token.raw`: the
  // lexer normalizes a list's raw text (a trailing space on its last item, which a stream ends on
  // after almost every word), and trusting it would drop to a single block on every such update.
  const blocks: Array<string> = []
  let offset = 0
  let blockStart = 0
  let seenBlock = false
  for (const token of new Lexer({ gfm: true }).blockTokens(source, [])) {
    if (token.type === 'def') {
      return [source]
    }
    const tokenStart = offset
    offset += token.raw.length
    // Blank-line runs stay on the block before them (leading ones on the first), so a block ends
    // where the next one starts.
    if (token.type === 'space') {
      continue
    }
    if (seenBlock) {
      blocks.push(source.slice(blockStart, tokenStart))
      blockStart = tokenStart
    }
    seenBlock = true
  }
  if (offset > source.length) {
    return [source]
  }
  // The lexer can stop short of trailing whitespace; it belongs to the last block.
  blocks.push(source.slice(blockStart))
  return blocks
}

const FENCE_OPEN = /^ {0,3}(`{3,}|~{3,})/

/**
 * Closes a `` ` ``, `**` or `*` the stream has opened but not yet closed, so the text renders as
 * code, bold or italic from its first word instead of showing the literal markers and then switching
 * element (which re-renders, and re-fades, everything after them). Meant for the tail block of a
 * stream; a fence is left alone, since its contents are literal.
 *
 * Covers what summaries write, not all of CommonMark: `_` emphasis, `***` runs and double-backtick
 * code still show their markers until they close. Each costs one re-fade of a few words.
 */
export function closeOpenInlineMarkers(block: string): string {
  if (FENCE_OPEN.test(block)) {
    return block
  }
  const body = block.trimEnd()
  const trailing = block.slice(body.length)
  let closed = body
  for (const marker of ['`', '**']) {
    const open = marker === '`' ? closed.split('`').length - 1 : countOutsideCode(closed, '**')
    if (open % 2 === 0) continue
    // A marker with nothing after it yet would render literally; hold it back instead.
    closed = closed.endsWith(marker) ? closed.slice(0, -marker.length) : closed + marker
  }
  if (/(?:^|\s)\*$/.test(closed)) {
    // A lone `*` the stream ends on renders literally until its word arrives; hold it back.
    closed = closed.slice(0, -1)
  } else if (hasOpenEmphasis(closed)) {
    closed += '*'
  }
  return closed + trailing
}

/**
 * Whether a single `*` has opened emphasis that hasn't closed. Code spans, `**` runs and bullets
 * don't count, nor does a `*` with space after it, which markdown leaves literal.
 */
function hasOpenEmphasis(text: string): boolean {
  // Code spans become a placeholder, so a `*` beside one still sees a character there.
  const prose = text
    .split('`')
    .map((part, index) => (index % 2 === 0 ? part : 'c'))
    .join('')
  let open = false
  for (const run of prose.matchAll(/\*+/g)) {
    const at = run.index
    if (run[0].length !== 1) continue
    const before = prose[at - 1] ?? '\n'
    const after = prose[at + 1] ?? ''
    if (/^[ \t]*$/.test(prose.slice(prose.lastIndexOf('\n', at - 1) + 1, at)) && /[ \t]/.test(after)) continue
    if (open && !/\s/.test(before)) open = false
    else if (!open && after !== '' && !/\s/.test(after)) open = true
  }
  return open
}

function countOutsideCode(text: string, marker: string): number {
  return text
    .split('`')
    .filter((_, index) => index % 2 === 0)
    .reduce((count, part) => count + (part.split(marker).length - 1), 0)
}

/**
 * A line holding only a block opener so far: a heading, bullet, ordered, quote or task marker, and
 * perhaps the inline markers its first word opens with, which render as nothing until it arrives.
 */
const BLOCK_MARKER_ONLY = /^[ \t]*(?:#{1,6}|[-*+]|\d{1,9}[.)]|>)(?:[ \t]+\[[ xX]?\]?)*(?:[ \t]+[*_`~]+)?[ \t]*$/

function wordEnd(text: string, from: number): number {
  const next = text.slice(from).search(/\s/)
  return next === -1 ? text.length : from + next
}

/**
 * Where a reveal reaching `position` should stop: the end of that word, and past it while the line
 * so far is only a block marker, or before the line if no word has followed the marker yet. A bare
 * `-` under a paragraph reads as a setext underline and a bare `1.` as paragraph text, so stopping
 * on one would change the block's type until the next word lands, remounting it and restarting its
 * fade.
 */
export function revealBoundary(text: string, position: number): number {
  let end = wordEnd(text, position)
  for (;;) {
    // Judge the last line with content: a reveal that has run past a marker line's newline would
    // otherwise see the empty line after it and publish the bare marker.
    let last = end
    while (last > 0 && /\s/.test(text[last - 1])) last--
    const lineStart = text.lastIndexOf('\n', last - 1) + 1
    if (!BLOCK_MARKER_ONLY.test(text.slice(lineStart, last))) return end
    let next = last
    while (next < text.length && /[ \t]/.test(text[next])) next++
    // Nothing after the marker yet: hold the whole line back until its first word arrives.
    if (next === text.length || text[next] === '\n') return lineStart
    end = wordEnd(text, next)
  }
}
